import { ApiError } from './ApiError';

/**
 * The one dictionary: backend error code → a sentence for the user (backend #682 decision 2).
 *
 * **One for the whole app, not one per feature** (#178). The codes are a single backend-wide
 * camelCase namespace out of one file — `ErrorCodes` in `Teamstep.GetLocal.Common`, plus the
 * normalized FluentValidation rule names — so mirroring it as one map is what keeps the two
 * diffable. A feature-local map would translate `notEmpty` in five places and let them drift.
 *
 * **An unknown code returns null on purpose.** The caller then shows its own generic message,
 * exactly as before this existed, and the code goes to the console. That is the whole migration
 * strategy: the backend's vocabulary grows without this file, and a code nobody has translated
 * yet degrades to what the user saw yesterday instead of to a blank toast or a raw `camelCase`
 * identifier.
 *
 * Two vocabularies deliberately stay out of here. The checkout gate's reasons live in
 * `OrderValidation.ts`, where the wording is per-position and parameterised with quantities and
 * currencies; and the wire is not the only source of wording for an enum — see the display maps
 * beside `ReservationStatus`, `InvoiceType` and the rest.
 */
export function errorCodeText(error: ApiError | null | undefined): string | null {
  if (!error?.code) {
    return null;
  }
  const p = error.params ?? {};

  switch (error.code) {
    // ---- normalized FluentValidation rules, and the model binder's own codes ----
    // These carry no field-specific wording because they are attached to a field: placed at the
    // control they belong to, "Dieses Feld ist erforderlich." reads correctly; in a toast the
    // caller's own message supplies the context.
    case 'notEmpty':
    case 'notNull':
    case 'required':
      return $localize`:@@error.required:Dieses Feld ist erforderlich.`;

    case 'maximumLength':
      return p['maxLength'] != null
        ? $localize`:@@error.maximumLength:Höchstens ${p['maxLength']}:maxLength: Zeichen.`
        : $localize`:@@error.tooLong:Der Wert ist zu lang.`;

    case 'minimumLength':
      return p['minLength'] != null
        ? $localize`:@@error.minimumLength:Mindestens ${p['minLength']}:minLength: Zeichen.`
        : $localize`:@@error.tooShort:Der Wert ist zu kurz.`;

    case 'exactLength':
      return $localize`:@@error.exactLength:Genau ${p['maxLength']}:length: Zeichen erforderlich.`;

    case 'emailValidator':
    case 'email':
      return $localize`:@@error.email:Bitte geben Sie eine gültige E-Mail-Adresse ein.`;

    case 'inclusiveBetween':
      return $localize`:@@error.inclusiveBetween:Bitte geben Sie einen Wert zwischen ${p['from']}:from: und ${p['to']}:to: an.`;

    case 'greaterThan':
      return $localize`:@@error.greaterThan:Der Wert muss größer als ${p['comparisonValue']}:comparisonValue: sein.`;

    case 'greaterThanOrEqual':
      return $localize`:@@error.greaterThanOrEqual:Der Wert muss mindestens ${p['comparisonValue']}:comparisonValue: sein.`;

    case 'lessThan':
      return $localize`:@@error.lessThan:Der Wert muss kleiner als ${p['comparisonValue']}:comparisonValue: sein.`;

    case 'lessThanOrEqual':
      return $localize`:@@error.lessThanOrEqual:Der Wert darf höchstens ${p['comparisonValue']}:comparisonValue: sein.`;

    case 'regularExpression':
      return $localize`:@@error.regularExpression:Das Format des Wertes ist ungültig.`;

    case 'invalidJson':
    case 'invalid':
      return $localize`:@@error.invalid:Die Anfrage war fehlerhaft. Bitte prüfen Sie Ihre Eingaben.`;

    // ---- cross-cutting (ErrorCodes) ----
    case 'idMismatch':
    case 'requestNull':
    case 'exactlyOneOf':
      return $localize`:@@error.badRequest:Die Anfrage war unvollständig oder widersprüchlich. Bitte laden Sie die Seite neu.`;

    case 'referenceUnknown':
      return $localize`:@@error.referenceUnknown:Der ausgewählte Eintrag existiert nicht mehr. Bitte laden Sie die Seite neu.`;

    case 'urlAbsoluteHttp':
      return $localize`:@@error.urlAbsoluteHttp:Bitte geben Sie eine vollständige Adresse an, die mit http:// oder https:// beginnt.`;

    case 'currencyIso4217':
      return $localize`:@@error.currencyIso4217:Bitte geben Sie eine dreibuchstabige Währung an, zum Beispiel EUR.`;

    case 'languageIso639':
    case 'languageUnsupported':
      return $localize`:@@error.languageUnsupported:Diese Sprache wird nicht unterstützt.`;

    case 'translationNameInvalid':
      return $localize`:@@error.translationNameInvalid:Die Übersetzung darf nicht leer und nicht zu lang sein.`;

    // #178 slice F / backend #701: the curation surface's own two.
    // `languageNotEditable` is the German column: it IS the entity's own text, seeded from it so
    // the two cannot drift, and for a category it is also the category resolver's input.
    case 'languageNotEditable':
      return $localize`:@@error.languageNotEditable:Diese Sprache stammt aus den Daten selbst und kann hier nicht bearbeitet werden.`;

    case 'referenceTypeUnknown':
      return $localize`:@@error.referenceTypeUnknown:Diese Art von Stammdaten ist unbekannt.`;

    case 'upstreamUnavailable':
      return $localize`:@@error.upstreamUnavailable:Ein benötigter Dienst ist gerade nicht erreichbar. Bitte versuchen Sie es später erneut.`;

    case 'actingSubjectRequired':
      return $localize`:@@error.actingSubjectRequired:Ihre Sitzung ist nicht mehr gültig. Bitte melden Sie sich erneut an.`;

    // ---- customer domain ----
    // #165 / #159: the rule whose message could not be placed at a field before this existed.
    case 'salutationInvalid':
      return $localize`:@@error.salutationInvalid:Bitte wählen Sie eine Anrede aus der Liste.`;

    case 'nameRequired':
      return $localize`:@@error.nameRequired:Bitte geben Sie mindestens einen der Werte Vorname, Nachname oder Firmenname an.`;

    case 'smallBusinessRequiresRetailer':
      return $localize`:@@error.smallBusinessRequiresRetailer:Die Kleinunternehmerregelung ist nur für Händler möglich.`;

    case 'retailerRequiresNetInvoicing':
      return $localize`:@@error.retailerRequiresNetInvoicing:Händler rechnen netto ab — bitte wählen Sie den Rechnungstyp „netto“.`;

    case 'grossInvoicingRequired':
      return $localize`:@@error.grossInvoicingRequired:Für dieses Kundenkonto ist die Bruttoabrechnung erforderlich.`;

    case 'settingScopeNotWritable':
      return $localize`:@@error.settingScopeNotWritable:Diese Einstellung kann hier nicht geändert werden.`;

    case 'settingUnknown':
      return $localize`:@@error.settingUnknown:Diese Einstellung ist unbekannt.`;

    case 'settingValueInvalid':
      return $localize`:@@error.settingValueInvalid:Der Wert dieser Einstellung ist ungültig.`;

    case 'bookmarkLimitReached':
      return $localize`:@@error.bookmarkLimitReached:Ihre Merkliste ist voll (maximal ${p['maxBookmarksPerProfile']}:max: Einträge). Bitte entfernen Sie einen Eintrag.`;

    // ---- user / registration ----
    case 'emailAlreadyInUse':
      return $localize`:@@error.emailAlreadyInUse:Diese E-Mail-Adresse wird bereits verwendet.`;

    case 'passwordEmpty':
      return $localize`:@@error.passwordEmpty:Bitte geben Sie ein Passwort ein.`;

    case 'passwordPolicy':
      return $localize`:@@error.passwordPolicy:Das Passwort erfüllt die Sicherheitsanforderungen nicht.`;

    case 'userNotFound':
      return $localize`:@@error.userNotFound:Dieser Benutzer wurde nicht gefunden.`;

    case 'userAlreadyActive':
      return $localize`:@@error.userAlreadyActive:Dieser Benutzer ist bereits aktiv.`;

    case 'primaryUsersOnly':
      return $localize`:@@error.primaryUsersOnly:Das ist nur der Hauptbenutzerin bzw. dem Hauptbenutzer des Kundenkontos möglich.`;

    // Backend #704 / PR #727: PUT api/profile refuses as data what used to be a 500. The two
    // field rules name `Status` / `Role`, so a form whose controls carry those names gets them
    // placed; the last-admin rule names no field and holds for every caller, the Carrier included.
    case 'profileLastAdmin':
      return $localize`:@@error.profileLastAdmin:Dieser Benutzer ist der letzte freigegebene Administrator des Kunden; Rolle und Status können nicht geändert werden.`;

    case 'profileStatusInvalid':
      return $localize`:@@error.profileStatusInvalid:Dieser Status ist unbekannt. Bitte wählen Sie einen Status aus der Liste.`;

    case 'profileRoleInvalid':
      return $localize`:@@error.profileRoleInvalid:Diese Rolle ist unbekannt. Bitte wählen Sie eine Rolle aus der Liste.`;

    // ---- catalog domain ----
    case 'categoryIdsDuplicate':
      return $localize`:@@error.categoryIdsDuplicate:Eine Kategorie wurde mehrfach ausgewählt.`;

    case 'categoryIdEmpty':
    case 'categoryUnknown':
      return $localize`:@@error.categoryUnknown:Eine ausgewählte Kategorie existiert nicht mehr. Bitte laden Sie die Seite neu.`;

    case 'netPriceRequired':
      return $localize`:@@error.netPriceRequired:Für diesen Rechnungstyp ist ein Nettopreis erforderlich.`;

    case 'grossPriceRequired':
      return $localize`:@@error.grossPriceRequired:Für diesen Rechnungstyp ist ein Bruttopreis erforderlich.`;

    case 'vatRequired':
      return $localize`:@@error.vatRequired:Bitte wählen Sie einen Mehrwertsteuersatz.`;

    case 'retailerVatIdRequired':
      return $localize`:@@error.retailerVatIdRequired:Für Händler ist eine Umsatzsteuer-Identifikationsnummer erforderlich.`;

    case 'smallBusinessNoVatId':
      return $localize`:@@error.smallBusinessNoVatId:Als Kleinunternehmen wird keine Umsatzsteuer-Identifikationsnummer angegeben.`;

    case 'productNumberRequired':
      return $localize`:@@error.productNumberRequired:Bitte geben Sie eine Artikelnummer an.`;

    case 'useUpdateForExisting':
      return $localize`:@@error.useUpdateForExisting:Dieser Eintrag existiert bereits.`;

    case 'vocabularyDuplicate':
      return $localize`:@@error.vocabularyDuplicate:Dieser Begriff ist für dieses Thema bereits hinterlegt.`;

    case 'vocabularySubjectUnknown':
    case 'vocabularyTargetUnknown':
      return $localize`:@@error.vocabularyTargetUnknown:Thema oder Ziel dieses Begriffs ist unbekannt.`;

    // ---- catalog sync (Carrier screens) ----
    case 'bindingDuplicate':
      return $localize`:@@error.bindingDuplicate:Für diese Adresse existiert bereits eine Anbindung.`;

    case 'defaultProductCategoryInvalid':
      return $localize`:@@error.defaultProductCategoryInvalid:Die Standardkategorie ist ungültig.`;

    case 'providerKeyUnknown':
    case 'vatProviderKeyUnknown':
    case 'currencyProviderKeyUnknown':
    case 'resolutionProfileUnknown':
      return $localize`:@@error.providerKeyUnknown:Die ausgewählte Variante „${p['key']}:key:“ ist unbekannt.`;

    // ---- GTIN ----
    case 'gtinEmpty':
      return $localize`:@@error.gtinEmpty:Bitte geben Sie eine GTIN ein.`;

    case 'gtinNotNumeric':
      return $localize`:@@error.gtinNotNumeric:Eine GTIN besteht nur aus Ziffern.`;

    case 'gtinInvalidLength':
      return $localize`:@@error.gtinInvalidLength:Eine GTIN hat 8, 12, 13 oder 14 Ziffern.`;

    case 'gtinPackagingUnit':
      return $localize`:@@error.gtinPackagingUnit:Diese GTIN bezeichnet ein Gebinde, keinen Einzelartikel.`;

    // ---- geo (backend #678 / web #172): the located search and the place lookup ----
    // One parser answers for every host (`GeoAreaParser`), so these are the whole vocabulary. A
    // client that builds its query from `geoToHttpParams` never sends an incomplete or ambiguous
    // area; the entries exist so a stale tab or a hand-edited URL still gets a sentence.
    case 'geoAreaIncomplete':
    case 'geoAreaAmbiguous':
    case 'geoBoundsInvalid':
      return $localize`:@@error.geoAreaInvalid:Der Suchbereich ist ungültig. Bitte wählen Sie Standort oder Umkreis neu.`;

    case 'geoLatitudeOutOfRange':
    case 'geoLongitudeOutOfRange':
      return $localize`:@@error.geoPositionOutOfRange:Die Position liegt außerhalb des gültigen Bereichs. Bitte wählen Sie den Standort neu.`;

    case 'geoRadiusOutOfRange':
      return p['max'] != null
        ? $localize`:@@error.geoRadiusOutOfRange:Der Umkreis muss größer als 0 und darf höchstens ${p['max']}:max: km sein.`
        : $localize`:@@error.geoRadiusInvalid:Der Umkreis ist ungültig.`;

    case 'sortByRequiresPosition':
      return $localize`:@@error.sortByRequiresPosition:Die Sortierung nach Entfernung ist nur mit einem Standort möglich.`;

    case 'geoPlaceRequired':
      return $localize`:@@error.geoPlaceRequired:Bitte geben Sie eine Postleitzahl oder einen Ort an.`;

    // ---- cart ----
    case 'ownListing':
      return $localize`:@@error.ownListing:Sie können Ihr eigenes Angebot nicht bestellen.`;

    case 'cartEmpty':
      return $localize`:@@error.cartEmpty:Ihr Warenkorb ist leer.`;

    default:
      return null;
  }
}
